import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import mermaid from "mermaid";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { useArtifactLineage } from "./useArtifactLineage";

interface ArtifactLineageProps {
  artifactId: string;
}

// Lazy-init mermaid once per module load. The legacy LineageTab does this
// inside an effect; doing it at module scope is cheaper and equivalent
// because mermaid.initialize is idempotent.
let mermaidReady = false;
function ensureMermaid() {
  if (mermaidReady) return;
  mermaid.initialize({ startOnLoad: false, theme: "default" });
  mermaidReady = true;
}

// Renders the mermaid diagram returned by the lineage endpoint as inline
// SVG. Mounts as the body of an ArtifactShell when the toolbar's Lineage
// toggle is on — exact parallel to ArtifactCode.
export const ArtifactLineage = ({ artifactId }: ArtifactLineageProps) => {
  const scope = useConversationContext();
  const { lineage, error, isLoading } = useArtifactLineage(scope, artifactId);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    if (!lineage?.mermaid || !container) return;
    ensureMermaid();
    const diagramId = `lineage-${artifactId}-${Date.now()}`;

    // Render into a fixed off-screen sandbox so mermaid's temporary DOM
    // insertion doesn't trigger the browser's scroll anchor algorithm and
    // cause the chat to jump when the Lineage tab is activated.
    const sandbox = document.createElement("div");
    sandbox.style.cssText = "position:fixed;top:0;left:-9999px;visibility:hidden";
    document.body.appendChild(sandbox);

    let cancelled = false;
    mermaid
      .render(diagramId, lineage.mermaid, sandbox)
      .then(({ svg }) => {
        if (cancelled) return;
        container.innerHTML = svg;
        setRenderError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRenderError(err instanceof Error ? err.message : "Failed to render lineage diagram");
      })
      .finally(() => {
        if (document.body.contains(sandbox)) {
          document.body.removeChild(sandbox);
        }
      });
    return () => {
      cancelled = true;
      if (document.body.contains(sandbox)) {
        document.body.removeChild(sandbox);
      }
    };
  }, [lineage, artifactId, container]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2
          className="size-5 animate-spin text-muted-foreground"
          aria-label="Loading lineage"
        />
      </div>
    );
  }
  if (error) {
    return <p className="py-3 text-xs text-destructive">Failed to load lineage: {String(error)}</p>;
  }
  if (!lineage || !lineage.nodes?.length) {
    return (
      <p className="py-3 text-xs text-muted-foreground">No lineage found for this artifact.</p>
    );
  }
  if (renderError) {
    return <p className="py-3 text-xs text-destructive">{renderError}</p>;
  }
  return (
    <>
      <p className="mb-3 text-xs text-muted-foreground">
        This diagram shows how this artifact was created, including all parent artifacts and source
        tables that contributed to it.
      </p>
      <div
        ref={setContainer}
        className="flex items-center justify-center overflow-x-auto rounded-sm border border-border bg-background p-4"
      />
    </>
  );
};
